import {
  formControlVariants,
  type FormControlVariantProps,
} from '@/shared/components/ui/form-control-styles';
import { ClearFieldButton } from '@/shared/components/ui/clear-field-button';
import { cn } from '@/shared/lib/utils';
import * as React from 'react';

/**
 * 너비·flex는 지우기 X가 붙는 래퍼에 둔다.
 * input에만 w-*를 주면 래퍼는 w-full이라 X가 셀 끝에서 잘린다.
 */
const WRAPPER_LAYOUT_CLASS_RE =
  /^(?:(?:sm|md|lg|xl|2xl):)?(?:min-w-|max-w-|w-|flex-|shrink-?|grow-?|basis-)/;

const splitInputClassName = (className?: string) => {
  const layout: string[] = [];
  const rest: string[] = [];
  for (const token of className?.split(/\s+/).filter(Boolean) ?? []) {
    if (WRAPPER_LAYOUT_CLASS_RE.test(token)) {
      layout.push(token);
    } else {
      rest.push(token);
    }
  }
  return {
    layoutClassName: layout.join(' '),
    inputClassNameRest: rest.join(' '),
  };
};

const UNCLEARABLE_TYPES = new Set([
  'file',
  'hidden',
  'checkbox',
  'radio',
  'button',
  'submit',
  'reset',
  'image',
  'range',
]);

export interface InputProps
  extends Omit<React.ComponentProps<'input'>, 'size'>,
    FormControlVariantProps {
  /** false면 값 있어도 X를 숨긴다. 비밀번호 토글 등과 겹칠 때 사용 */
  clearable?: boolean;
  /** 입력 테두리 안쪽 왼쪽 아이콘. 있으면 패딩을 input에 준다 */
  startIcon?: React.ReactNode;
  /** 실제 input에만 붙는 클래스 (비밀번호 토글용 pr-9 등) */
  inputClassName?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      type = 'text',
      variant,
      size,
      tone,
      clearable = true,
      startIcon,
      inputClassName,
      disabled,
      readOnly,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref,
  ) => {
    const isControlled = value !== undefined;
    const [uncontrolled, setUncontrolled] = React.useState(
      String(defaultValue ?? ''),
    );
    const currentValue = isControlled ? String(value ?? '') : uncontrolled;
    const canClear = clearable && !UNCLEARABLE_TYPES.has(type);
    const showClear =
      canClear && !disabled && !readOnly && currentValue.length > 0;
    // X 유무로 wrapper를 껐다 켜면 input이 remount되어 두 번째 글자부터 입력이 끊긴다
    const needsAdornment = Boolean(startIcon) || canClear;
    const { layoutClassName, inputClassNameRest } =
      splitInputClassName(className);

    const input = (
      <input
        ref={ref}
        type={type}
        disabled={disabled}
        readOnly={readOnly}
        {...(isControlled ? { value } : { defaultValue })}
        onChange={(event) => {
          if (!isControlled) setUncontrolled(event.target.value);
          onChange?.(event);
        }}
        className={cn(
          formControlVariants({ variant, size, tone }),
          type === 'number' &&
            '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none',
          startIcon && 'pl-9',
          // X를 테두리·라운드 안쪽에 두기 위해 우측 패딩을 넉넉히
          showClear && 'pr-10',
          inputClassName,
          needsAdornment ? 'min-w-0 w-full' : layoutClassName,
          inputClassNameRest,
        )}
        {...props}
      />
    );

    if (!needsAdornment) return input;

    return (
      <div
        className={cn(
          'relative flex min-w-0 w-full max-w-full',
          layoutClassName,
        )}
      >
        {startIcon ? (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute left-3 top-1/2 z-10 flex size-4 -translate-y-1/2 items-center justify-center',
              'text-ruda-text-tertiary [&_svg]:size-4',
              disabled && 'text-ruda-text-disabled',
            )}
          >
            {startIcon}
          </span>
        ) : null}
        {input}
        {showClear ? (
          <ClearFieldButton
            onClick={() => {
              if (!isControlled) setUncontrolled('');
              onChange?.({
                target: { value: '' },
                currentTarget: { value: '' },
              } as React.ChangeEvent<HTMLInputElement>);
            }}
          />
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';

export { Input };
